import { supabase } from '../lib/supabase';

// Assessment Timeline Management
export const assessmentTimelineService = {
  // Get all assessment timelines with filtering
  async getTimelines(filters = {}) {
    let query = supabase?.from('assessment_timelines')?.select(`
        *,
        unit:units(id, code, name, program:programs(id, name, department:departments(id, name))),
        semester:academic_semesters(id, semester_number, semester_type, start_date, end_date, is_active, program_class_id),
        creator:user_profiles!assessment_timelines_created_by_fkey(id, full_name, primary_role)
      `)?.order('created_at', { ascending: false });

    if (filters?.unit_id) {
      query = query?.eq('unit_id', filters?.unit_id);
    }
    if (filters?.semester_id) {
      query = query?.eq('semester_id', filters?.semester_id);
    }
    if (filters?.assessment_type) {
      query = query?.eq('assessment_type', filters?.assessment_type);
    }
    if (filters?.assessment_status) {
      query = query?.eq('assessment_status', filters?.assessment_status);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching assessment timelines:', error);
      return { data: null, error };
    }

    // Calendar semester support was added after the legacy academic-semester
    // link. Enrich rows opportunistically so the service remains readable on
    // deployments that have not applied the new column yet.
    const calendarIds = [...new Set((data || []).map(row => row.calendar_semester_id).filter(Boolean))];
    if (calendarIds.length > 0) {
      const { data: calendarPeriods } = await supabase
        .from('semesters')
        .select('id, name, academic_year_id, start_date, end_date, status')
        .in('id', calendarIds);
      const byId = new Map((calendarPeriods || []).map(period => [String(period.id), period]));
      return { data: data.map(row => ({ ...row, calendar_period: byId.get(String(row.calendar_semester_id)) || null })), error: null };
    }

    return { data, error: null };
  },

  // Create new assessment timeline
  async createTimeline(timelineData) {
    const { data, error } = await supabase?.from('assessment_timelines')?.insert({
        ...timelineData,
        created_by: (await supabase?.auth?.getUser())?.data?.user?.id
      })?.select(`
        *,
        unit:units(id, code, name),
        semester:academic_semesters(id, semester_number, semester_type, start_date, end_date, is_active, program_class_id)
      `)?.single();

    if (error) {
      console.error('Error creating assessment timeline:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Update assessment timeline
  async updateTimeline(timelineId, updateData) {
    const { data, error } = await supabase?.from('assessment_timelines')?.update(updateData)?.eq('id', timelineId)?.select(`
        *,
        unit:units(id, code, name),
        semester:academic_semesters(id, semester_number, semester_type, start_date, end_date, is_active, program_class_id)
      `)?.single();

    if (error) {
      console.error('Error updating assessment timeline:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Delete assessment timeline
  async deleteTimeline(timelineId) {
    const { error } = await supabase?.from('assessment_timelines')?.delete()?.eq('id', timelineId);

    if (error) {
      console.error('Error deleting assessment timeline:', error);
      return { error };
    }

    return { error: null };
  }
};

// Assessment Documents Management
export const assessmentDocumentService = {
  // Get documents for a timeline
  async getDocuments(timelineId) {
    const { data, error } = await supabase?.from('assessment_documents')?.select(`
        *,
        uploader:user_profiles!assessment_documents_uploaded_by_fkey(id, full_name, primary_role)
      `)?.eq('timeline_id', timelineId)?.order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Error fetching assessment documents:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Upload document
  async uploadDocument(timelineId, file, documentType) {
    const user = await supabase?.auth?.getUser();
    const userId = user?.data?.user?.id;

    if (!userId) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Generate file path
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${documentType}_${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${timelineId}/${fileName}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('assessment-documents')?.upload(filePath, file, { upsert: true });

    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      return { data: null, error: uploadError };
    }

    // Save document record
    const { data, error } = await supabase?.from('assessment_documents')?.insert({
        timeline_id: timelineId,
        document_type: documentType,
        file_path: uploadData?.path,
        file_name: file?.name,
        file_size: file?.size,
        mime_type: file?.type,
        uploaded_by: userId
      })?.select(`
        *,
        uploader:user_profiles!assessment_documents_uploaded_by_fkey(id, full_name, primary_role)
      `)?.single();

    if (error) {
      console.error('Error saving document record:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get signed URL for document
  async getDocumentUrl(filePath) {
    const { data, error } = await supabase?.storage?.from('assessment-documents')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      console.error('Error getting document URL:', error);
      return { data: null, error };
    }

    return { data: data?.signedUrl, error: null };
  },

  // Delete document
  async deleteDocument(documentId, filePath) {
    // Delete from storage
    const { error: storageError } = await supabase?.storage?.from('assessment-documents')?.remove([filePath]);

    if (storageError) {
      console.error('Error deleting file from storage:', storageError);
    }

    // Delete record
    const { error } = await supabase?.from('assessment_documents')?.delete()?.eq('id', documentId);

    if (error) {
      console.error('Error deleting document record:', error);
      return { error };
    }

    return { error: null };
  }
};

// Student Assessment Management
export const studentAssessmentService = {
  // Get student assessments
  async getStudentAssessments(filters = {}) {
    let query = supabase?.from('student_assessments')?.select(`
        *,
        timeline:assessment_timelines(
          *,
          unit:units(id, code, name),
          semester:academic_semesters(id, semester_number, semester_type)
        ),
        student:user_profiles!student_assessments_student_id_fkey(id, full_name, student_id),
        unit_registration:student_unit_registrations(id, registration_date)
      `)?.order('created_at', { ascending: false });

    if (filters?.timeline_id) {
      query = query?.eq('timeline_id', filters?.timeline_id);
    }
    if (filters?.student_id) {
      query = query?.eq('student_id', filters?.student_id);
    }
    if (filters?.submission_status) {
      query = query?.eq('submission_status', filters?.submission_status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching student assessments:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get assessment by student and timeline
  async getStudentAssessment(timelineId, studentId) {
    const { data, error } = await supabase?.from('student_assessments')?.select(`
        *,
        timeline:assessment_timelines(
          *,
          unit:units(id, code, name)
        ),
        evidence:assessment_evidence(*)
      `)?.eq('timeline_id', timelineId)?.eq('student_id', studentId)?.single();

    if (error) {
      console.error('Error fetching student assessment:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Update assessment marks
  async updateMarks(assessmentId, marksData) {
    const { raw_marks, total_marks, assessment_type } = marksData;
    
    // Calculate converted marks based on assessment type
    let converted_marks;
    if (assessment_type === 'formative') {
      converted_marks = total_marks > 0 ? Math.round((raw_marks / total_marks) * 60 * 100) / 100 : 0;
    } else {
      converted_marks = total_marks > 0 ? Math.round((raw_marks / total_marks) * 40 * 100) / 100 : 0;
    }

    // Calculate rating and grade letter
    let rating, grade_letter;
    if (converted_marks >= 80) {
      rating = '5';
      grade_letter = 'A';
    } else if (converted_marks >= 65) {
      rating = '4';
      grade_letter = 'B';
    } else if (converted_marks >= 50) {
      rating = '3';
      grade_letter = 'C';
    } else if (converted_marks >= 30) {
      rating = '2';
      grade_letter = 'D';
    } else {
      rating = '1';
      grade_letter = 'F';
    }

    const { data, error } = await supabase?.from('student_assessments')?.update({
        raw_marks,
        converted_marks,
        grade_letter,
        rating,
        submission_status: 'submitted'
      })?.eq('id', assessmentId)?.select('*')?.single();

    if (error) {
      console.error('Error updating assessment marks:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Create student assessment record
  async createStudentAssessment(assessmentData) {
    const { data, error } = await supabase?.from('student_assessments')?.insert(assessmentData)?.select(`
        *,
        timeline:assessment_timelines(
          *,
          unit:units(id, code, name)
        )
      `)?.single();

    if (error) {
      console.error('Error creating student assessment:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Assessment Evidence Management
export const assessmentEvidenceService = {
  // Upload evidence
  async uploadEvidence(studentAssessmentId, file) {
    const user = await supabase?.auth?.getUser();
    const userId = user?.data?.user?.id;

    if (!userId) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Generate file path
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `evidence_${Date.now()}.${fileExt}`;
    const filePath = `${userId}/evidence/${fileName}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('assessment-evidence')?.upload(filePath, file, { upsert: true });

    if (uploadError) {
      console.error('Error uploading evidence file:', uploadError);
      return { data: null, error: uploadError };
    }

    // Save evidence record
    const { data, error } = await supabase?.from('assessment_evidence')?.insert({
        student_assessment_id: studentAssessmentId,
        file_path: uploadData?.path,
        file_name: file?.name,
        file_size: file?.size,
        mime_type: file?.type
      })?.select('*')?.single();

    if (error) {
      console.error('Error saving evidence record:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get evidence for assessment
  async getEvidence(studentAssessmentId) {
    const { data, error } = await supabase?.from('assessment_evidence')?.select('*')?.eq('student_assessment_id', studentAssessmentId)?.order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Error fetching assessment evidence:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get signed URL for evidence
  async getEvidenceUrl(filePath) {
    const { data, error } = await supabase?.storage?.from('assessment-evidence')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      console.error('Error getting evidence URL:', error);
      return { data: null, error };
    }

    return { data: data?.signedUrl, error: null };
  }
};

// Assessment Approval Management
export const assessmentApprovalService = {
  // Get approvals for assessment
  async getApprovals(studentAssessmentId) {
    const { data, error } = await supabase?.from('assessment_approvals')?.select(`
        *,
        approver:user_profiles!assessment_approvals_approver_id_fkey(id, full_name, primary_role)
      `)?.eq('student_assessment_id', studentAssessmentId)?.order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching assessment approvals:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Submit approval
  async submitApproval(approvalData) {
    const user = await supabase?.auth?.getUser();
    const userId = user?.data?.user?.id;

    const { data, error } = await supabase?.from('assessment_approvals')?.insert({
        ...approvalData,
        approver_id: userId,
        approved_at: approvalData?.approval_status !== 'pending' ? new Date()?.toISOString() : null
      })?.select(`
        *,
        approver:user_profiles!assessment_approvals_approver_id_fkey(id, full_name, primary_role)
      `)?.single();

    if (error) {
      console.error('Error submitting approval:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Summative Assessment Eligibility
export const summativeEligibilityService = {
  // Get student eligibility
  async getStudentEligibility(studentId, unitId) {
    const { data, error } = await supabase?.from('summative_eligibility')?.select(`
        *,
        student:user_profiles!summative_eligibility_student_id_fkey(id, full_name, student_id),
        unit:units(id, code, name),
        semester_activation:student_semester_activations(*)
      `)?.eq('student_id', studentId)?.eq('unit_id', unitId)?.single();

    if (error) {
      console.error('Error fetching eligibility:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Check or create eligibility record
  async checkEligibility(studentId, unitId, semesterActivationId) {
    // First try to get existing record
    let { data, error } = await supabase?.from('summative_eligibility')?.select('*')?.eq('student_id', studentId)?.eq('unit_id', unitId)?.eq('semester_activation_id', semesterActivationId)?.single();

    if (error && error?.code === 'PGRST116') {
      // Record doesn't exist, create it
      const { data: newData, error: createError } = await supabase?.from('summative_eligibility')?.insert({
          student_id: studentId,
          unit_id: unitId,
          semester_activation_id: semesterActivationId
        })?.select('*')?.single();

      if (createError) {
        console.error('Error creating eligibility record:', createError);
        return { data: null, error: createError };
      }

      data = newData;
    } else if (error) {
      console.error('Error checking eligibility:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Update eligibility criteria
  async updateEligibility(eligibilityId, updateData) {
    const { data, error } = await supabase?.from('summative_eligibility')?.update(updateData)?.eq('id', eligibilityId)?.select('*')?.single();

    if (error) {
      console.error('Error updating eligibility:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Assessment Fees Management
export const assessmentFeesService = {
  // Get active fees
  async getActiveFees() {
    const { data, error } = await supabase?.from('assessment_fees')?.select('*')?.eq('is_active', true)?.order('unit_type', { ascending: true });

    if (error) {
      console.error('Error fetching assessment fees:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get fee for specific criteria
  async getFee(unitType, assessmentType) {
    const { data, error } = await supabase?.from('assessment_fees')?.select('*')?.eq('unit_type', unitType)?.eq('assessment_type', assessmentType)?.eq('is_active', true)?.single();

    if (error) {
      console.error('Error fetching specific fee:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Assessment Bookings Management
export const assessmentBookingService = {
  // Get student bookings
  async getStudentBookings(studentId) {
    const { data, error } = await supabase?.from('assessment_bookings')?.select(`
        *,
        timeline:assessment_timelines(
          *,
          unit:units(id, code, name)
        ),
        eligibility:summative_eligibility(*)
      `)?.eq('student_id', studentId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching student bookings:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Create booking
  async createBooking(bookingData) {
    const { data, error } = await supabase?.from('assessment_bookings')?.insert(bookingData)?.select(`
        *,
        timeline:assessment_timelines(
          *,
          unit:units(id, code, name)
        )
      `)?.single();

    if (error) {
      console.error('Error creating booking:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Update booking payment status
  async updatePaymentStatus(bookingId, paymentData) {
    const { data, error } = await supabase?.from('assessment_bookings')?.update({
        payment_status: paymentData?.status,
        payment_reference: paymentData?.reference,
        paid_at: paymentData?.status === 'paid' ? new Date()?.toISOString() : null
      })?.eq('id', bookingId)?.select('*')?.single();

    if (error) {
      console.error('Error updating payment status:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Assessment Analytics
export const assessmentAnalyticsService = {
  // Get analytics for timeline
  async getTimelineAnalytics(timelineId) {
    const { data, error } = await supabase?.from('assessment_analytics')?.select('*')?.eq('timeline_id', timelineId)?.single();

    if (error) {
      console.error('Error fetching timeline analytics:', error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Get department analytics
  async getDepartmentAnalytics(departmentId) {
    const { data, error } = await supabase?.from('assessment_analytics')?.select(`
        *,
        timeline:assessment_timelines(
          unit:units(
            program:programs(department_id)
          )
        )
      `)?.eq('timeline.unit.program.department_id', departmentId);

    if (error) {
      console.error('Error fetching department analytics:', error);
      return { data: null, error };
    }

    return { data, error: null };
  }
};

// Real-time subscriptions
export const assessmentRealtimeService = {
  // Subscribe to assessment timeline changes
  subscribeToTimelines(callback) {
    const channel = supabase?.channel('assessment_timelines')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'assessment_timelines' },
        callback
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Subscribe to student assessment changes
  subscribeToStudentAssessments(timelineId, callback) {
    const channel = supabase?.channel(`student_assessments_${timelineId}`)?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'student_assessments',
          filter: `timeline_id=eq.${timelineId}`
        },
        callback
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Subscribe to approval changes
  subscribeToApprovals(studentAssessmentId, callback) {
    const channel = supabase?.channel(`approvals_${studentAssessmentId}`)?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'assessment_approvals',
          filter: `student_assessment_id=eq.${studentAssessmentId}`
        },
        callback
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

export default {
  timeline: assessmentTimelineService,
  documents: assessmentDocumentService,
  studentAssessment: studentAssessmentService,
  evidence: assessmentEvidenceService,
  approval: assessmentApprovalService,
  eligibility: summativeEligibilityService,
  fees: assessmentFeesService,
  booking: assessmentBookingService,
  analytics: assessmentAnalyticsService,
  realtime: assessmentRealtimeService
};
